import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { useProfile } from "../hooks/use-profile";
import { FileRejection, useDropzone } from "react-dropzone";
import { useCallback, useState } from "react";
import { toast } from "sonner";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { api } from "@/lib/axios";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { authKeys } from "@/features/auth/queries/auth.queries";

const ACCEPTED_FILE_TYPES = {
  "image/*": [".jpeg", ".jpg", ".png", ".gif"],
};

// 5MB
const MAX_FILE_SIZE = 5 * 1024 * 1024;

// 1 file
const MAX_FILES = 1;

export function ProfileForm({ user }: { user: any }) {
  const { form, onSubmit, isPending } = useProfile(user);
  const [previewImage, setPreviewImage] = useState<string | null>(
    user.image || null
  );
  const queryClient = useQueryClient();

  const { mutateAsync, isPending: isUploading } = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append("files", file);

      const response = await api.post("/api/user/pfp", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      formData.delete("files");
      return response.data;
    },
    onError: (error) => {
      if (error instanceof AxiosError) {
        toast.error(error.response?.data.message);
      } else {
        toast.error("Something went wrong while updating photo");
      }
    },
    onSuccess: (data) => {
      // Create object URL for immediate preview update
      if (data.image) {
        console.log(data.image);
        setPreviewImage(data.image);
      }
      queryClient.invalidateQueries({
        queryKey: authKeys.me(),
      });
      toast.success("Profile Image Updated Successfully");
    },
  });

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];

      try {
        await mutateAsync(file);
      } catch (error) {
        setPreviewImage(user.image || null);
      }
    },
    [mutateAsync, user.image]
  );

  const onDropRejected = useCallback((fileRejections: FileRejection[]) => {
    if (fileRejections[0].file.size > MAX_FILE_SIZE) {
      toast.error(
        `File size should be less than ${MAX_FILE_SIZE / 1024 / 1024}MB`
      );
    } else if (fileRejections[0].file.type !== "image/*") {
      toast.error("File type is not supported");
    } else {
      toast.error("File is not supported");
    }
  }, []);

  const { getRootProps, getInputProps } = useDropzone({
    accept: ACCEPTED_FILE_TYPES,
    maxSize: MAX_FILE_SIZE,
    maxFiles: MAX_FILES,
    onDrop: onDrop,
    onDropRejected,
    disabled: isPending || isUploading,
  });

  const initials = `${user.firstName?.[0] ?? ""}${user.lastName?.[0] ?? ""}`;

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex flex-col gap-6"
      >
        <Card>
          <CardHeader>
            <CardTitle>Profile Settings</CardTitle>
          </CardHeader>
          <CardContent>
            <div
              {...getRootProps({ className: "dropzone" })}
              className="size-16"
            >
              <input {...getInputProps()} />
              <div className="flex items-center gap-4">
                <Avatar
                  className={cn("h-16 w-16", {
                    "cursor-pointer": !isUploading,
                  })}
                >
                  {isUploading ? (
                    <Loader2 className="mx-auto h-8 w-8 animate-spin text-primary" />
                  ) : (
                    <>
                      <AvatarImage
                        className="object-cover"
                        src={previewImage || ""}
                      />
                      <AvatarFallback className="bg-primary/10 text-primary">
                        {initials || "U"}
                      </AvatarFallback>
                    </>
                  )}
                </Avatar>
              </div>
            </div>

            <Separator className="my-4" />

            <div className="grid gap-6 md:grid-cols-2">
              <div className="space-y-4">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={user.email ?? ""}
                  readOnly
                  className="bg-muted text-muted-foreground cursor-not-allowed"
                  placeholder="Enter Your Email"
                />
              </div>

              <FormField
                name="firstName"
                control={form.control}
                render={({ field }) => (
                  <FormItem className="space-y-2">
                    <FormLabel htmlFor="First Name">First Name</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="First name"
                        type="text"
                        className="bg-muted"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                name="lastName"
                control={form.control}
                render={({ field }) => (
                  <FormItem className="space-y-2">
                    <FormLabel htmlFor="Last Name">Last Name</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Last name"
                        type="text"
                        className="bg-muted"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                name="instituteName"
                control={form.control}
                render={({ field }) => (
                  <FormItem className="space-y-2">
                    <FormLabel htmlFor="Institution Name">
                      Institution Name
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Institution name"
                        type="text"
                        className="bg-muted"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </CardContent>
          <CardFooter className="justify-end">
            <Button
              disabled={isPending || !form.formState.isDirty}
              type="submit"
            >
              {isPending ? "Saving..." : "Save"}
            </Button>
          </CardFooter>
        </Card>
      </form>
    </Form>
  );
}
